import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { LogInIcon, RefreshCwIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../../service/ApiClient';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';

// Error codes the backend's magic-login endpoint returns (per contract).
// Anything else — network failure, 500, a missing/absent token, or an
// unrecognized code — falls back to the generic "network" case below.
const KNOWN_ERROR_CODES = ['invalid', 'expired', 'already_used'];

/**
 * Redemption page for the one-click auto-signin link emailed to trial
 * prospects: https://.../auth/magic-login?token=<token>&hotel=<HOTELCODE>
 *
 * Public route — must be reachable while logged out (see src/App.js).
 *
 * The token is single-use, so this component:
 *   - POSTs it (never a plain GET navigation/fetch of a link), since email
 *     clients and security scanners prefetch GET links and would silently
 *     burn the token before the human ever clicks it.
 *   - Strips it from the URL immediately via history.replaceState so it
 *     doesn't linger in browser history or leak via the Referer header.
 *   - Is never logged to the console.
 */
const MagicLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'welcome_failed'
  const [errorCode, setErrorCode] = useState(null);
  const attempted = useRef(false);

  const token = searchParams.get('token');
  const hotel = searchParams.get('hotel');
  // Set by the trial confirm step when the welcome email (username +
  // password) could not be sent: the person is signed in but has no
  // login details, so we say so before handing them the dashboard.
  const welcomeEmailFailed = searchParams.get('welcome_email') === 'failed';

  const attemptLogin = useCallback(async () => {
    setStatus('loading');
    setErrorCode(null);

    if (!token) {
      setStatus('error');
      setErrorCode('invalid');
      return;
    }

    try {
      await apiClient.magicLogin(token);
      if (welcomeEmailFailed) {
        setStatus('welcome_failed');
        return;
      }
      // Same landing spot as a normal successful login.
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const code = error?.response?.data?.code || error?.code;
      setStatus('error');
      setErrorCode(KNOWN_ERROR_CODES.includes(code) ? code : 'network');
    }
  }, [token, navigate, welcomeEmailFailed]);

  useEffect(() => {
    // Strip token/hotel from the URL immediately — before the POST even
    // resolves — so a single-use token never lingers in browser history
    // or leaks to a third party via the Referer header.
    window.history.replaceState({}, document.title, window.location.pathname);

    if (attempted.current) return;
    attempted.current = true;
    attemptLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToLogin = () => navigate('/login');
  const goToDashboard = () => navigate('/dashboard', { replace: true });

  const errorCopy = {
    expired: {
      title: t('auth.magicLogin.errors.expiredTitle', 'This link has expired'),
      message: t(
        'auth.magicLogin.errors.expiredMessage',
        "This sign-in link has expired (they're valid for 24 hours). You can still sign in with the username and password from the same email."
      ),
    },
    already_used: {
      title: t('auth.magicLogin.errors.usedTitle', 'Link already used'),
      message: t(
        'auth.magicLogin.errors.usedMessage',
        'This link has already been used. Sign in with the username and password from your email.'
      ),
    },
    invalid: {
      title: t('auth.magicLogin.errors.invalidTitle', 'Link not valid'),
      message: t('auth.magicLogin.errors.invalidMessage', "This sign-in link isn't valid."),
    },
    network: {
      title: t('auth.magicLogin.errors.networkTitle', 'Something went wrong'),
      message: t(
        'auth.magicLogin.errors.networkMessage',
        'Something went wrong signing you in. Please check your connection and try again.'
      ),
    },
  };

  const { title, message } = errorCopy[errorCode] || errorCopy.invalid;

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgGradient="linear(to-br, blue.50, white, blue.50)"
    >
      <Container maxW="md" p={8} bg="white" borderRadius="lg" boxShadow="lg" position="relative">
        <Box position="absolute" top={3} right={3}>
          <LanguageSwitcher size="xs" variant="ghost" />
        </Box>

        {status === 'welcome_failed' ? (
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="lg" mb={2}>{t('app.name', 'FrontDesko')}</Heading>
            </Box>
            <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <AlertIcon />
                <Text fontWeight="semibold">
                  {t('auth.magicLogin.welcomeFailedTitle', "You're signed in — but we couldn't email your login details")}
                </Text>
              </Box>
              <Text fontSize="sm">
                {t(
                  'auth.magicLogin.welcomeFailedMessage',
                  'Our email service did not accept the welcome message with your username and password. Please set a password now in Settings → Users so you can sign in again later. Our team has been notified.'
                )}
              </Text>
            </Alert>
            <Button colorScheme="blue" leftIcon={<LogInIcon size={16} />} onClick={goToDashboard}>
              {t('auth.magicLogin.continueToDashboard', 'Continue to dashboard')}
            </Button>
          </VStack>
        ) : status === 'loading' ? (
          <VStack spacing={5} py={4}>
            <Heading size="lg">{t('app.name', 'FrontDesko')}</Heading>
            <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
            <Text color="gray.500">{t('auth.magicLogin.signingIn', 'Signing you in…')}</Text>
            {hotel && (
              <Text fontSize="xs" color="gray.400">
                {hotel}
              </Text>
            )}
          </VStack>
        ) : (
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={2}>
              <Heading size="lg" mb={2}>{t('app.name', 'FrontDesko')}</Heading>
            </Box>

            <Alert status="warning" borderRadius="md" flexDirection="column" alignItems="flex-start" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <AlertIcon />
                <Text fontWeight="semibold">{title}</Text>
              </Box>
              <Text fontSize="sm">{message}</Text>
            </Alert>

            <VStack spacing={3}>
              <Button
                colorScheme="blue"
                size="lg"
                width="full"
                leftIcon={<LogInIcon size={20} />}
                onClick={goToLogin}
              >
                {t('auth.magicLogin.actions.goToLogin', 'Go to login')}
              </Button>
              {errorCode === 'network' && (
                <Button
                  variant="outline"
                  size="lg"
                  width="full"
                  leftIcon={<RefreshCwIcon size={18} />}
                  onClick={attemptLogin}
                >
                  {t('auth.magicLogin.actions.retry', 'Try again')}
                </Button>
              )}
            </VStack>
          </VStack>
        )}
      </Container>
    </Box>
  );
};

export default MagicLogin;
